// Admin → Families. Read-only list of every family with parent + kid count +
// subscription. docs/replan-v2.md (admin console, slice 2).
// Migrated to the console component system in M7c / P6.

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import {
  DataTable,
  FilterBar,
  PageHeader,
  Pagination,
  StatusBadge,
  actionsColumn,
  paginate,
  sortRows,
  useTableState,
  type Column,
} from '~/components';
import { listFamilies } from '~/lib/admin';

export const Route = createFileRoute('/_authed/families/')({
  component: FamiliesPage,
});

type FamilyRow = Awaited<ReturnType<typeof listFamilies>>[number];

function FamiliesPage() {
  const navigate = useNavigate();
  const table = useTableState({
    defaultSort: { key: 'createdAt', direction: 'desc' },
    filterKeys: ['subscription'],
  });

  const families = useQuery({
    queryKey: ['families'],
    queryFn: ({ signal }) => listFamilies(signal),
  });

  const filtered = useMemo(() => {
    let rows = families.data ?? [];
    const sub = table.filters.subscription;
    if (sub === 'subscribed') rows = rows.filter((f) => f.subscriptionStatus != null);
    else if (sub === 'none') rows = rows.filter((f) => f.subscriptionStatus == null);

    const needle = table.q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((f) => f.parentLabel.toLowerCase().includes(needle));
  }, [families.data, table.q, table.filters.subscription]);

  const sorted = useMemo(
    () =>
      sortRows(filtered, table.sort, {
        parentLabel: (f) => f.parentLabel,
        kidCount: (f) => f.kidCount,
        status: (f) => f.status,
        subscriptionStatus: (f) => f.subscriptionStatus,
        createdAt: (f) => f.createdAt,
      }),
    [filtered, table.sort],
  );

  const pageRows = useMemo(
    () => paginate(sorted, table.page, table.pageSize),
    [sorted, table.page, table.pageSize],
  );

  const columns: Column<FamilyRow>[] = [
    {
      key: 'parentLabel',
      header: 'Parent',
      sortable: true,
      alwaysVisible: true,
      render: (f) => (
        <Link
          to="/families/$id"
          params={{ id: f.id }}
          onClick={(e) => e.stopPropagation()}
          className="truncate font-medium text-admin-900 hover:underline"
        >
          {f.parentLabel}
        </Link>
      ),
    },
    {
      key: 'kidCount',
      header: 'Kids',
      sortable: true,
      align: 'end',
      render: (f) => <span className="text-admin-700">{f.kidCount}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (f) => <StatusBadge status={f.status} dot />,
    },
    {
      key: 'subscriptionStatus',
      header: 'Subscription',
      sortable: true,
      render: (f) =>
        f.subscriptionStatus ? (
          <StatusBadge status={f.subscriptionStatus} dot />
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
    {
      key: 'createdAt',
      header: 'Joined',
      sortable: true,
      align: 'end',
      hideBelow: 'md',
      render: (f) => <span className="text-xs text-admin-500">{formatDate(f.createdAt)}</span>,
    },
    actionsColumn<FamilyRow>({
      label: (f) => f.parentLabel,
      primary: (f) => ({
        id: 'open',
        label: 'Open',
        icon: 'edit',
        onSelect: () => void navigate({ to: '/families/$id', params: { id: f.id } }),
      }),
      actions: (f) => [
        {
          id: 'subscription',
          label: 'View subscription',
          icon: 'card',
          // useTableState reads window.location.search directly, so the query
          // has to be in the URL — TanStack's `search` option needs a route
          // that declares validateSearch, which these list routes do not.
          onSelect: () =>
            window.location.assign(`/subscriptions?q=${encodeURIComponent(f.parentLabel)}`),
          disabled: f.subscriptionStatus == null,
          disabledReason: 'This family has no subscription yet.',
        },
        {
          id: 'payments',
          label: 'View invoices',
          icon: 'receipt',
          onSelect: () =>
            window.location.assign(`/payments?q=${encodeURIComponent(f.parentLabel)}`),
        },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Families" description="Every family account — parent + kids." />

      <DataTable
        columns={columns}
        rows={pageRows}
        rowKey={(f) => f.id}
        loading={families.isLoading}
        error={families.error}
        onRetry={() => void families.refetch()}
        sort={table.sort}
        onSortChange={table.setSort}
        onRowClick={(f) => void navigate({ to: '/families/$id', params: { id: f.id } })}
        columnVisibility
        caption="Families"
        empty={{
          icon: 'family',
          title: table.isFiltered ? 'No families match these filters' : 'No families yet',
          description: table.isFiltered
            ? 'Try clearing the filters to see everything.'
            : 'Families appear here as parents sign up.',
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Search by parent…"
            filters={[
              {
                key: 'subscription',
                label: 'Subscription',
                value: table.filters.subscription ?? '',
                options: [
                  { value: 'subscribed', label: 'Has one' },
                  { value: 'none', label: 'None' },
                ],
              },
            ]}
            onFilterChange={table.setFilter}
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
        footer={
          <Pagination
            page={table.page}
            pageSize={table.pageSize}
            total={sorted.length}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        }
      />
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
